/* eslint-disable no-restricted-syntax */ function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import getErrorCode from '../../utils/getErrorCode';
const telemetryCache = new WeakMap();
const wrappedCache = new WeakMap();
// Generating a `Response` object to wrap a fetch error requires an HTTP code
// in the [200, 599] range, so we use an unused code to avoid conflicts with
// other legitimate codes.
export const NETWORK_ERROR = 457;
// Even weirder, to emulate GraphQL we need to create a success code that wraps
// the error (GraphQL returns HTTP 200 with errors inside and that's OK, but
// since this is "fake" GraphQL engineers get confused when they see a response
// code of 257).
export const NETWORK_SUCCESS = 257;
function createObjectWithHiddenProps(shown, hidden) {
    const result = {};
    for(const key in hidden){
        if (hidden.hasOwnProperty(key)) {
            let value = hidden[key];
            Object.defineProperty(result, key, {
                configurable: true,
                enumerable: true,
                get () {
                    return value;
                },
                set (newValue) {
                    value = newValue;
                }
            });
        }
    }
    for(const key in shown){
        if (shown.hasOwnProperty(key)) {
            result[key] = shown[key];
        }
    }
    return result;
}
export function fetchWrapper(fetch) {
    if (!fetch) {
        return fetch;
    }
    const wrappedFetch = wrappedCache.get(fetch);
    if (wrappedFetch) {
        return wrappedFetch;
    }
    const newWrappedFetch = function() {
        var _withErrorConversion = _async_to_generator(function*(url, options = {}, ...args) {
            let response;
            try {
                response = yield fetch(url, options);
                // Parse the response as text. This may fail with a TypeError too if the
                // stream is broken and we want to catch it as a network error (see
                // https://fetch.spec.whatwg.org/#concept-body-consume-body).
                yield response.clone().text();
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Unknown error';
                const data = {
                    code: 'network_error',
                    message: `Native fetch error: ${message}`
                };
                response = new Response(JSON.stringify(data), {
                    status: NETWORK_ERROR
                });
            }
            if (!response.ok) {
                var _response_headers;
                let body;
                // @ts-expect-error Manually modified apollo-link-rest to pass field as an extra arg.
                const field = args[0];
                try {
                    body = yield response.clone().json();
                } catch (err) {
                    body = {};
                }
                const info = {
                    body,
                    meta: {
                        field,
                        status: response.status === NETWORK_ERROR ? -1 : response.status,
                        requestId: (_response_headers = response.headers) === null || _response_headers === void 0 ? void 0 : _response_headers.get('Request-Id'),
                        url
                    }
                };
                response = new Response(JSON.stringify({
                    __typename: info
                }), {
                    status: NETWORK_SUCCESS,
                    statusText: response.statusText,
                    headers: response.headers
                });
            }
            return response;
        });
        function withErrorConversion(url) {
            return _withErrorConversion.apply(this, arguments);
        }
        return withErrorConversion;
    }();
    wrappedCache.set(fetch, newWrappedFetch);
    return newWrappedFetch;
}
export function rebuildErrorFromResponse(maybeBody, maybeMeta) {
    const maybeError = (maybeBody === null || maybeBody === void 0 ? void 0 : maybeBody.error) || maybeBody;
    const maybeCode = maybeError && getErrorCode(maybeError) || null;
    var _maybeError_message;
    const message = (_maybeError_message = maybeError === null || maybeError === void 0 ? void 0 : maybeError.message) !== null && _maybeError_message !== void 0 ? _maybeError_message : 'An unknown error has occurred.';
    const status = maybeMeta.status;
    // Create a base object that hides legacy properties. Legacy properties
    // are properties used by existing code but that will no longer exist
    // once V1 is migrated to GraphQL gateway.
    const legacyError = new Error(message);
    if (maybeError) {
        for(const key in maybeError){
            if (maybeError.hasOwnProperty(key)) {
                legacyError[key] = maybeError[key];
            }
        }
    }
    legacyError.http_status = status;
    legacyError.request_id = maybeMeta.requestId;
    legacyError.url = maybeMeta.url;
    if (maybeBody === null || maybeBody === void 0 ? void 0 : maybeBody.two_factor_options) {
        legacyError.two_factor_options = maybeBody.two_factor_options;
    }
    if (maybeBody === null || maybeBody === void 0 ? void 0 : maybeBody.two_factor_primary_method) {
        legacyError.two_factor_primary_method = maybeBody.two_factor_primary_method;
    }
    let source = '<unknown>';
    if (status <= 0) {
        source = 'network';
    }
    if (status >= 400 && status < 500) {
        source = 'client';
    }
    if (status >= 500 && status < 600) {
        source = legacyError.error_classification_for_frontend || 'server';
    }
    let code = 'could_not_get_error_code';
    if (status <= 0) {
        code = 'network_error';
    } else if (maybeCode) {
        code = maybeCode;
    }
    const fakeExtensions = {
        actionId: null,
        traceId: null,
        error: {
            code,
            source,
            status
        },
        // These props come from the backend but they don't come in the
        // HTTP body. V2 link extracts them too and puts them in the error
        // body so that the observability link can read them.
        __DO_NOT_USE_requestId: maybeMeta.requestId
    };
    const extensions = createObjectWithHiddenProps(fakeExtensions, (maybeError === null || maybeError === void 0 ? void 0 : maybeError.extensions) || {});
    const fakeGraphQlError = {
        message,
        locations: [
            {
                line: -1,
                column: -1
            }
        ],
        path: [
            maybeMeta.field
        ],
        extensions
    };
    const finalError = createObjectWithHiddenProps(fakeGraphQlError, legacyError);
    const telemetry = {
        status,
        url: maybeMeta.url ? `${maybeMeta.url}` : '',
        requestId: maybeMeta.requestId,
        body: maybeBody
    };
    telemetryCache.set(legacyError, telemetry);
    telemetryCache.set(finalError, telemetry);
    return finalError;
}
export function rebuildResponse(res) {
    const { data, errors } = res;
    const allErrors = [
        ...errors || []
    ];
    for(const key in data){
        if (data.hasOwnProperty(key)) {
            var _data_key;
            const maybeResponse = (_data_key = data[key]) === null || _data_key === void 0 ? void 0 : _data_key.__typename;
            if (typeof maybeResponse === 'object') {
                var _maybeResponse_body;
                if ((_maybeResponse_body = maybeResponse.body) === null || _maybeResponse_body === void 0 ? void 0 : _maybeResponse_body.errors) {
                    maybeResponse.body.errors.forEach((error)=>{
                        allErrors.push(rebuildErrorFromResponse(error, maybeResponse.meta));
                    });
                } else {
                    allErrors.push(rebuildErrorFromResponse(maybeResponse.body, maybeResponse.meta));
                }
                data[key] = null;
            }
        }
    }
    return {
        data,
        errors: allErrors.length ? allErrors : undefined
    };
}

//# sourceMappingURL=errorConversion.js.map